import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import {
  useMountTransition,
  useScrollTransition,
  useUnMountTransition,
} from 'util/react-transition-component';
import { useChangeTransitionState } from '../../../util/react-transition-component/hooks/useChangeTransitionState';
import ScrollTransition from '../../unlisted/scroll-transition/ScrollTransition';
import type { HeadingProps, HeadingRef } from './Heading.data';
import styles from './Heading.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './Heading.transitions';
import { useIsChineseLanguage } from '../../../hooks/useIsChineseLanguage';
import useWindowSize from '../../../hooks/useWindowSize';
import { useStoryblokEditorContext } from '../../unlisted/StoryblokEditor';

export default function Heading({
  as = 'h1',
  size,
  className,
  childClassName,
  children,
  isVisible,
  transitionInType = 'splitCharsSlideInUp',
  transitionOutType = 'fadeOut',
  disableTriggerInOnScroll = true,
  transitionOnMount = false,
  transitionOnUnMount = false,
  parallax,
  canTransitionIn,
  scrollTransitionVars,
  enableChineseTransitionFallback = false,
  ref,
  ...props
}: HeadingProps & { ref?: Ref<HeadingRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HeadingRef | null>;
  const isChineseLanguage = useIsChineseLanguage();
  const { isInEditorMode } = useStoryblokEditorContext();

  const { width: windowWidth } = useWindowSize();

  // For lengthy words (e.g. entertainment) reduce fontsize so it fits the allocated space
  useEffect(() => {
    if (!elementRef?.current) return;

    gsap.set(elementRef?.current, { clearProps: 'fontSize' });
    const maxWidth = elementRef.current?.clientWidth || 0;
    let bigChildWidth = 1;

    if (elementRef.current?.children) {
      [...elementRef.current.children].some((child) => {
        if (child.scrollWidth > maxWidth) {
          bigChildWidth = child.scrollWidth;
        }
        return child.scrollWidth > maxWidth;
      });
    }

    if (bigChildWidth > 1) {
      const currentFontSize = parseInt(window.getComputedStyle(elementRef.current).fontSize, 10);
      gsap.set(elementRef.current, {
        fontSize: (currentFontSize * maxWidth) / bigChildWidth,
      });
    }
  }, [elementRef, windowWidth]);

  const { transitionController } = useScrollTransition({
    scroll: {
      canTransitionIn,
      disableTriggerInOnScroll,
      trigger: elementRef,
      vars: scrollTransitionVars,
    },
    setupOptions: () => ({
      ref: elementRef,
      refs: {
        elementRef,
        transitionInType: isInEditorMode ? 'none' : transitionInType,
        transitionOutType: isInEditorMode ? 'none' : transitionOutType,
        enableChineseTransitionFallback,
        isChineseLanguage,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
  });

  useMountTransition(transitionController, transitionOnMount);
  useUnMountTransition(transitionController, transitionOnUnMount);
  useChangeTransitionState(transitionController, isVisible);

  const Tag = as;
  const innerClassName = parallax ? childClassName : className;

  const innerTemplate = (
    <Tag
      key={isInEditorMode ? 'editor_mode' : ''}
      ref={elementRef as RefObject<(HTMLHeadingElement & HTMLTimeElement & HTMLLIElement) | null>}
      className={classNames(styles.heading, styles[size], innerClassName)}
      {...props}
    >
      {children}
    </Tag>
  );

  let template = innerTemplate;
  if (parallax) {
    template = (
      <ScrollTransition
        as="div"
        type="parallax"
        config={parallax.config}
        vars={parallax.vars}
        trigger={parallax.trigger}
        className={classNames(className)}
      >
        {innerTemplate}
      </ScrollTransition>
    );
  }

  return template;
}
